import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useMemo } from "react";
import { User } from "@common/types/user";
import { Picker } from "@design-system/Picker/Picker.lib";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import type { ApplyCloseTemplateEntitySelectionRow } from "../applyCloseTemplateEntitySelection.types";
import { HStack } from "@design-system/HStack/HStack";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { useTemplateApplication } from "../useTemplateApplication";

export const ApplyCloseEntityClientCell = memo(
    function ApplyCloseEntityClientCell({
        row,
    }: CellContext<
        ViewTableFeatures,
        ApplyCloseTemplateEntitySelectionRow,
        string
    >) {
        const {
            getEntityRow,
            getMemberLabel,
            setEntityRowField,
            workspaceMembersMap,
        } = useTemplateApplication();
        const item = row.original;

        const idStr = item.entityIdStr;
        const rowState = idStr ? getEntityRow(idStr) : undefined;
        const clientId = rowState?.clientId ?? null;

        const membersForWorkspace = useMemo(
            () => workspaceMembersMap[item.workspaceId] ?? [],
            [workspaceMembersMap, item.workspaceId],
        );

        const selectedMember = useMemo(
            () =>
                clientId != null
                    ? (membersForWorkspace.find(
                          (m) => m.user.id === clientId,
                      ) ?? null)
                    : null,
            [clientId, membersForWorkspace],
        );

        const validClientId = selectedMember ? clientId : null;

        const getWorkspaceMemberLabel = useCallback(
            (member: { user: User }) => {
                const fullName =
                    `${member.user.firstName ?? ""} ${member.user.lastName ?? ""}`.trim();
                return fullName || getMemberLabel(member.user.id);
            },
            [getMemberLabel],
        );

        const handleChange = useCallback(
            (id: User["id"] | null) => {
                if (!idStr) {
                    return;
                }
                if (id == null) {
                    setEntityRowField(idStr, "clientId", null);
                    return;
                }
                const hasWorkspaceAccess = membersForWorkspace.some(
                    (m) => m.user.id === id,
                );
                if (!hasWorkspaceAccess) {
                    return;
                }
                setEntityRowField(idStr, "clientId", id);
            },
            [idStr, membersForWorkspace, setEntityRowField],
        );

        if (item.isClientRow || !idStr) {
            return null;
        }

        if (!row.original.isApplicationSelected || !rowState) {
            return (
                <ViewTableCell>
                    <span className="text-muted">-</span>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell>
                <Picker.Select<User["id"]>
                    value={validClientId}
                    onValueChange={handleChange}
                >
                    <Picker.CellContent
                        value={
                            selectedMember && (
                                <HStack gap={8} align="center">
                                    <UserAvatar
                                        user={selectedMember.user}
                                        size="sm"
                                        aria-hidden
                                    />
                                    <BreakableText
                                        text={getWorkspaceMemberLabel(
                                            selectedMember,
                                        )}
                                        showTooltip
                                    />
                                </HStack>
                            )
                        }
                        aria-label={
                            selectedMember
                                ? getWorkspaceMemberLabel(selectedMember)
                                : undefined
                        }
                        addText="Assign client"
                        hideAddIcon
                    />
                    <Picker.Popup>
                        <Picker.List>
                            <Picker.Item onSelect={() => handleChange(null)}>
                                No client
                            </Picker.Item>
                            {membersForWorkspace.map((member) => (
                                <Picker.Item
                                    key={member.user.id}
                                    value={member.user.id}
                                    leading={
                                        <UserAvatar
                                            user={member.user}
                                            size="xs"
                                            aria-hidden
                                        />
                                    }
                                >
                                    {getWorkspaceMemberLabel(member)}
                                </Picker.Item>
                            ))}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            </ViewTableCell>
        );
    },
);
